import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LogoIcon } from '@/components/landing/Logo'

const schema = z
  .object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    password_confirmation: z.string().min(6, 'Password must be at least 6 characters'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type FormValues = z.infer<typeof schema>

interface Props {
  errors: Record<string, string[]>
}

export default function SignUp({ errors }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors: fieldErrors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  function onSubmit(data: FormValues) {
    router.post('/sign_up', { user: data })
  }

  function serverError(field: string): string | undefined {
    return errors[field]?.[0]
  }

  return (
    <div className="relative isolate flex min-h-screen items-center justify-center overflow-hidden p-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 engineer-grid opacity-30"
        style={{
          maskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-[480px] w-[680px] -translate-x-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(ellipse at center, hsl(var(--brand-2) / 0.18) 0%, transparent 60%)',
        }}
      />

      <div className="surface-card w-full max-w-sm p-8">
        <Link href="/" className="mb-6 inline-flex items-center gap-2 font-heading text-base font-semibold tracking-tight">
          <LogoIcon />
          KonexZero
        </Link>

        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Get started
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight">
          Create an account
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign up to start using KonexZero. Free tier — no card required.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              className="h-11 rounded-md border-foreground/10 bg-background/50"
              {...register('email')}
            />
            {(fieldErrors.email?.message || serverError('email')) && (
              <p className="text-xs text-destructive">
                {fieldErrors.email?.message ?? serverError('email')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              className="h-11 rounded-md border-foreground/10 bg-background/50"
              {...register('password')}
            />
            {(fieldErrors.password?.message || serverError('password')) && (
              <p className="text-xs text-destructive">
                {fieldErrors.password?.message ?? serverError('password')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password_confirmation" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Confirm password
            </Label>
            <Input
              id="password_confirmation"
              type="password"
              autoComplete="new-password"
              className="h-11 rounded-md border-foreground/10 bg-background/50"
              {...register('password_confirmation')}
            />
            {(fieldErrors.password_confirmation?.message ||
              serverError('password_confirmation')) && (
              <p className="text-xs text-destructive">
                {fieldErrors.password_confirmation?.message ??
                  serverError('password_confirmation')}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-11 w-full rounded-md text-sm font-semibold"
          >
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link href="/sign_in" className="font-medium text-foreground underline underline-offset-4 hover:text-primary">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
